import { VISIBILITY_MODES, type QuestionVisibilityPolicy } from '@aysar/contracts';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';

import { SectionVisibilityDefaultsMatrix } from '../components/SectionVisibilityDefaultsMatrix';
import { type SectionVisibilityPatch } from '../utils/question-visibility/question-visibility.util';

/** The contract nests the patch under `visibility`, exactly as the single write does. */
const VISIBILITY_FIELD = 'visibility';

/** Translates a key, with interpolation. The caller's `t`. */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The section-wide defaults form: one field, because the value IS the patch.
 *
 * Modelling it as four independent form fields was the obvious alternative and
 * is wrong: `SectionVisibilityDefaultsSchema` refines the object as a whole
 * ("name at least one part"), and four fields would each validate happily while
 * the body they add up to is refused by the api.
 */
export function buildSectionVisibilityDefaultsFields(t: Translate): readonly FieldDef[] {
  return [
    {
      name: VISIBILITY_FIELD,
      kind: 'custom',
      // The QUESTION the matrix answers, never the dialog's own title again: the
      // heading two lines above already says which section this is about, and a
      // caption repeating it printed one sentence twice — the editor's own
      // "Visibility" section vs "Who can see this answer" split, applied here.
      label: t('sections.visibilityDefaults.fieldLabel'),
      // It composes its own group name, so the form prints a caption rather than
      // a `<Label htmlFor>` pointing at an element with no id.
      isSelfNaming: true,
      // The contract's refinement message is an English constant inside
      // `libs/contracts`; under an Arabic label it would be untranslated copy
      // (U8), and only this field knows the sentence that belongs there.
      invalidMessage: t('sections.visibilityDefaults.nothingNamed'),
      // CALLED, never mounted as a component: an identity created during render
      // remounts the matrix on every pass (React Compiler `static-components`).
      render: (control) => (
        <SectionVisibilityDefaultsMatrix
          value={asPatch(control.value)}
          isDisabled={control.isDisabled}
          describedById={control.describedById}
          onChange={control.onChange}
        />
      ),
    },
  ];
}

function isMode(value: unknown): value is QuestionVisibilityPolicy['publicVisibility'] {
  return VISIBILITY_MODES.some((mode) => mode === value);
}

/**
 * Narrows the form's value to a patch.
 *
 * `unknown` at the boundary is what U4 permits and what this is: a `custom`
 * field receives whatever the form holds. Every facet is copied only when it is
 * genuinely present and of the right shape — an absent one must stay absent,
 * because absence is the payload's way of saying "leave this alone".
 */
function asPatch(value: unknown): SectionVisibilityPatch {
  if (typeof value !== 'object' || value === null) return {};

  const candidate = value as Record<string, unknown>;
  const patch: SectionVisibilityPatch = {};

  if (isMode(candidate['publicVisibility'])) patch.publicVisibility = candidate['publicVisibility'];
  if (isMode(candidate['matchedVisibility']))
    patch.matchedVisibility = candidate['matchedVisibility'];
  if (typeof candidate['defaultVisiblePublic'] === 'boolean')
    patch.defaultVisiblePublic = candidate['defaultVisiblePublic'];
  if (typeof candidate['defaultVisibleMatched'] === 'boolean')
    patch.defaultVisibleMatched = candidate['defaultVisibleMatched'];

  return patch;
}
